import { useQuery } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { chamarSeguradora, type Funcao } from "./funcoes";

/**
 * O que a plataforma grava no portal Azos com o login da corretora
 * (seguradoras-azos-portal pela porta `seguradoras`; histórico em seguros_acoes,
 * migration 303). Propostas,
 * apólices, leads e contrapropostas são lidas na hora (acao "consultar").
 * A function só age na conexão de quem chama.
 */

export type TipoAcao =
  | "nf_enviar"
  | "nf_informada"
  | "cotacao_arquivar"
  | "cotacao_duplicar"
  | "proposta_assinatura"
  | "proposta_reenviar_assinatura"
  | "proposta_arquivar"
  | "proposta_duplicar"
  | "proposta_link_cartao"
  | "proposta_pagamento"
  | "proposta_vencimento"
  | "apolice_pagamento"
  | "endosso"
  | "contraproposta_aceitar"
  | "contraproposta_recusar"
  | "lead_criar"
  | "lead_situacao"
  | "lead_nota";

/** Nome da ação no histórico e no título da confirmação. */
export const ROTULOS_ACAO: Record<TipoAcao, string> = {
  nf_enviar: "Envio de nota fiscal",
  nf_informada: "Nota fiscal informada",
  cotacao_arquivar: "Arquivamento de cotação",
  cotacao_duplicar: "Duplicação de cotação",
  proposta_assinatura: "Envio para assinatura",
  proposta_reenviar_assinatura: "Reenvio da assinatura",
  proposta_arquivar: "Arquivamento de proposta",
  proposta_duplicar: "Duplicação de proposta",
  proposta_link_cartao: "Link de cartão de crédito",
  proposta_pagamento: "Troca da forma de pagamento",
  proposta_vencimento: "Troca do vencimento",
  apolice_pagamento: "Troca da forma de pagamento da apólice",
  endosso: "Pedido de endosso",
  contraproposta_aceitar: "Aceite de contraproposta",
  contraproposta_recusar: "Recusa de contraproposta",
  lead_criar: "Novo lead",
  lead_situacao: "Situação do lead",
  lead_nota: "Nota no lead",
};

/** Histórico: as ações acima e as leituras registradas (teste do cotador). */
export const ROTULOS_HISTORICO: Record<string, string> = { ...ROTULOS_ACAO, cotador_diagnostico: "Teste do cotador" };

export type PassoCotador = { passo: string; status: number | null; erro?: string };

/** Só leitura no cotador da Azos (contratacao.azos.com.br): conta e a cotação salva mais recente. */
export const testarCotador = () =>
  chamarSeguradora<{ ok: boolean; passos: PassoCotador[] }>(
    "azos",
    "cotar",
    { acao: "cotador_diagnostico" },
    "testar o cotador da Azos",
  );

/** Função comum de cada ação (espelho de azos/portal_acoes.ts). */
export const FUNCAO_DA_ACAO: Record<TipoAcao, Funcao> = {
  nf_enviar: "nota_fiscal",
  nf_informada: "nota_fiscal",
  cotacao_arquivar: "cotacoes_salvas",
  cotacao_duplicar: "cotacoes_salvas",
  proposta_assinatura: "assinatura",
  proposta_reenviar_assinatura: "assinatura",
  proposta_arquivar: "propostas",
  proposta_duplicar: "propostas",
  proposta_link_cartao: "pagamento",
  proposta_pagamento: "pagamento",
  proposta_vencimento: "vencimento",
  apolice_pagamento: "pagamento",
  endosso: "endosso",
  contraproposta_aceitar: "exigencias",
  contraproposta_recusar: "exigencias",
  lead_criar: "leads",
  lead_situacao: "leads",
  lead_nota: "leads",
};

export const executarNoPortal = (tipo: TipoAcao, dados: Record<string, unknown>) =>
  chamarSeguradora<{ ok: boolean; url?: string }>(
    "azos",
    FUNCAO_DA_ACAO[tipo],
    { acao: "executar", tipo, dados },
    "enviar a ação ao portal da Azos",
  );

export type PropostaPortal = {
  id: string;
  apolice_id: string | null;
  segurado: string | null;
  telefone: string | null;
  situacao: string | null;
  situacao_texto: string | null;
  premio: number | null;
  frequencia: string | null;
  metodo: string | null;
  criada_em: string | null;
  motivo_recusa: string | null;
};

export type ApolicePortal = {
  id: string;
  numero: string | null;
  proposta_id: string | null;
  segurado: string | null;
  situacao: string | null;
  situacao_texto: string | null;
  premio: number | null;
  metodo: string | null;
  frequencia: string | null;
};

export type ApoliceDetalhe = ApolicePortal & { coberturas: { codigo: string; capital: number; premio: number }[] };

export type LeadPortal = {
  id: string;
  nome: string | null;
  telefone: string | null;
  email: string | null;
  situacao: string | null;
  notas: number;
  criado_em: string | null;
};

export type ContrapropostaPortal = {
  id: string;
  proposta_id: string | null;
  segurado: string | null;
  situacao: string | null;
  premio: number | null;
  criada_em: string | null;
};

type Recurso = "propostas" | "apolices" | "apolice" | "leads" | "contrapropostas" | "vencimentos";

/** Função comum de cada consulta (espelho de _shared/seguradoras/porta.ts). */
const FUNCAO_DA_CONSULTA: Record<Recurso, Funcao> = {
  propostas: "propostas",
  apolices: "acompanhar",
  apolice: "endosso",
  leads: "leads",
  contrapropostas: "exigencias",
  vencimentos: "vencimento",
};

export const consultarPortal = async <T>(recurso: Recurso, alvo?: string): Promise<T> => {
  const { dados, erro } = await chamarSeguradora<{ dados: T }>(
    "azos",
    FUNCAO_DA_CONSULTA[recurso],
    { acao: "consultar", recurso, alvo },
    "consultar o portal da Azos",
  );
  if (erro || !dados) throw new Error(erro ?? "Não foi possível consultar o portal da Azos.");
  return dados.dados;
};

/** Lista lida na hora do portal; só busca quando a seção abre. */
export const usePortalAzos = <T>(recurso: Recurso, habilitado: boolean) =>
  useQuery({
    queryKey: ["portal-azos", recurso],
    queryFn: () => consultarPortal<T[]>(recurso),
    enabled: habilitado,
    staleTime: 60_000,
    retry: false,
  });

/** users.id de quem está logado: as ações aparecem só na própria conexão. */
export const useMeuUsuarioCrm = () => {
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: ["usuario-crm", identity?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("users").select("id").eq("user_id", String(identity?.id)).maybeSingle();
      if (error) throw new Error(error.message);
      return data ? Number((data as { id: number }).id) : null;
    },
    enabled: identity?.id != null,
    staleTime: Infinity,
  });
};

export type AcaoRegistrada = {
  id: number;
  tipo: string;
  alvo: string | null;
  ok: boolean;
  status_http: number | null;
  erro: string | null;
  criado_em: string;
};

export const lerHistorico = async (crmUserId: number): Promise<AcaoRegistrada[]> => {
  const { data, error } = await supabase
    .from("seguros_acoes" as never)
    .select("id, tipo:operacao, alvo, ok, status_http, erro, criado_em")
    .eq("seguradora", "azos")
    .eq("crm_user_id", crmUserId)
    .order("criado_em", { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return (data ?? []) as AcaoRegistrada[];
};

const SITUACOES_PROPOSTA: Record<string, string> = {
  draft: "Em preenchimento",
  info_completed: "Em preenchimento",
  sign_process: "Aguardando assinatura",
  in_analysis: "Análise Azos",
  signed: "Gerando pagamento",
  pending_payment: "Aguardando pagamento",
  accepted: "Aceita",
  refused: "Recusada",
  expired: "Expirada",
  filed: "Arquivada",
  discontinued: "Descontinuada",
  payment_expired: "Pagamento expirado",
};

export const rotuloProposta = (p: Pick<PropostaPortal, "situacao" | "situacao_texto">) =>
  (p.situacao && SITUACOES_PROPOSTA[p.situacao]) ?? p.situacao_texto ?? p.situacao ?? "—";

const METODOS: Record<string, string> = { credit_card: "Cartão", pix: "Pix", bank_slip: "Boleto" };
export const rotuloMetodo = (m: string | null | undefined) => (m ? (METODOS[m] ?? m) : "—");

export const SITUACOES_LEAD = { to_be_contacted: "A contatar", lead: "Em negociação", archived: "Arquivado" } as const;
export const rotuloLead = (s: string | null | undefined) =>
  s && s in SITUACOES_LEAD ? SITUACOES_LEAD[s as keyof typeof SITUACOES_LEAD] : (s ?? "—");

/**
 * Ações que o portal oferece em cada situação da proposta (menu da proposta
 * no portal, 29/10/2026).
 */
export const acoesDaProposta = (situacao: string | null): TipoAcao[] => {
  switch (situacao) {
    case "draft":
    case "info_completed":
      return ["proposta_assinatura", "proposta_duplicar", "proposta_arquivar"];
    case "sign_process":
      return ["proposta_reenviar_assinatura", "proposta_duplicar", "proposta_arquivar"];
    case "pending_payment":
      return ["proposta_link_cartao", "proposta_pagamento", "proposta_vencimento", "proposta_duplicar"];
    case "refused":
    case "expired":
    case "payment_expired":
    case "discontinued":
      return ["proposta_duplicar", "proposta_arquivar"];
    default:
      return ["proposta_duplicar"];
  }
};

/** Arquivo → base64 (sem o prefixo data:). */
export const arquivoEmBase64 = (arquivo: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result).replace(/^data:[^,]*,/, ""));
    leitor.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    leitor.readAsDataURL(arquivo);
  });

export const NF_TIPOS = ["application/pdf", "text/xml", "application/xml", "image/png", "image/jpeg"];
export const NF_MAX_BYTES = 5 * 1024 * 1024;
